import { useState, forwardRef } from 'react';
import { motion } from 'framer-motion';
import type { WeeklyGoal } from '@/types';
import styles from '@/pages/WeeklyPlanningPage.module.css';

export interface WeeklyGoalItemProps {
  goal: WeeklyGoal;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
  onEdit: (id: string, title: string) => void;
}

export const WeeklyGoalItem = forwardRef<HTMLDivElement, WeeklyGoalItemProps>(
  function WeeklyGoalItem({ goal, onToggle, onRemove, onEdit }, ref) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(goal.title);

    const handleSubmit = () => {
      if (editValue.trim()) {
        onEdit(goal.id, editValue.trim());
      }
      setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSubmit();
      } else if (e.key === 'Escape') {
        setEditValue(goal.title);
        setIsEditing(false);
      }
    };

    return (
      <motion.div
        ref={ref}
        className={`${styles.goalItem} ${goal.completed ? styles.goalCompleted : ''}`}
        layout
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 10, height: 0 }}
      >
        <button
          className={styles.goalCheckbox}
          onClick={() => onToggle(goal.id)}
          aria-label={goal.completed ? 'Mark as incomplete' : 'Mark as complete'}
          aria-pressed={goal.completed}
        >
          {goal.completed && <span aria-hidden="true">&#10003;</span>}
        </button>

        {isEditing ? (
          <input
            type="text"
            className={styles.goalEditInput}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSubmit}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        ) : (
          <span className={styles.goalTitle} onDoubleClick={() => setIsEditing(true)}>
            {goal.title}
          </span>
        )}

        <button
          className={styles.goalRemoveBtn}
          onClick={() => onRemove(goal.id)}
          aria-label="Remove goal"
        >
          &times;
        </button>
      </motion.div>
    );
  }
);
